import parse, { domToReact } from 'html-react-parser';
import dynamic from 'next/dynamic';
import { uid } from 'react-uid';

import Heading from 'shopper/components/Heading';
import Text from 'shopper/components/Text';

import { toFallbackImageAltText } from 'lib/image';
import { forceHttps, sanitize, toInternalLink } from 'lib/links';
import { isUrlValidForInternalUsage } from 'lib/string';

import {
  doesAnyDomNodeParentContainName,
  hasLineBreakChild,
  isInlineText,
  isLineBreaker,
  strip,
  toChildrenWithoutLineBreaks,
} from './htmlParserUtils';

import HTML_PARSER from 'constants/htmlParser';

const NextLink = dynamic(() => import('next/link'));
const Anchor = dynamic(() => import('components/Anchor'));
const Image = dynamic(() => import('components/Image'));
const ListGroup = dynamic(() => import('components/List/ListGroup'));
const ListItem = dynamic(() => import('components/List/ListItem'));

const pageDescriptionOptions = {
  trim: true,
  replace: (domNode) => {
    const { name, attribs, children = [], data, type } = domNode ?? {};
    // No idea why a <o:p> tag is within the HTML
    if (name === 'o:p') {
      return <></>;
    }

    /**
     * We ignores unmapped nodes because some texts aren't wrapped to a tag.
     * So, because this, we needs explicit to return the text
     */
    if (isInlineText(type) && data && !isLineBreaker(data)) {
      if (
        doesAnyDomNodeParentContainName(
          domNode?.parent,
          HTML_PARSER.NAME_PARAGRAPH
        )
      ) {
        return <span>{strip(data)}</span>;
      }

      if (domNode?.parent === null) {
        return <Text>{strip(data)}</Text>;
      }

      return data.trim();
    }

    if (name === HTML_PARSER.NAME_SPAN) {
      return <span>{domToReact(children, pageDescriptionOptions)}</span>;
    }

    /**
     * - Header tags are wrapped inside <p> tags, we check for children instead
     * to remove this wrapper tag
     *
     * - In specific cases, multiple titles are wrapped by a unique Header tag,
     * so we separate these titles in different Header tags
     *
     * - Is transformed <h1 /> into <h2 /> to prevent the page has multiple
     * <h1 />
     */
    if (
      name === HTML_PARSER.NAME_H1 ||
      name === HTML_PARSER.NAME_H2 ||
      children[0]?.attribs?.style?.includes('36')
    ) {
      if (hasLineBreakChild(children)) {
        return (
          <>
            {toChildrenWithoutLineBreaks(children)?.map((child) => (
              <Heading
                key={uid(child)}
                as="h2"
                className="mb-2 mt-6"
                size="size3"
              >
                {child?.data?.trim() || child?.children[0]?.data?.trim()}
              </Heading>
            ))}
          </>
        );
      }

      return (
        <Heading as="h2" className="mb-2 mt-6" size="size3">
          {domToReact(children, pageDescriptionOptions)}
        </Heading>
      );
    }

    if (
      name === HTML_PARSER.NAME_H3 ||
      children[0]?.attribs?.style?.includes('24')
    ) {
      if (hasLineBreakChild(children)) {
        return (
          <>
            {toChildrenWithoutLineBreaks(children)?.map((child) => (
              <Heading
                key={uid(child)}
                as="h3"
                className="mb-2 mt-6"
                size="size4"
              >
                {child?.data?.trim() || child?.children[0]?.data?.trim()}
              </Heading>
            ))}
          </>
        );
      }

      return (
        <Heading as="h3" className="mb-2 mt-6" size="size4">
          {domToReact(children, pageDescriptionOptions)}
        </Heading>
      );
    }

    if (
      name === HTML_PARSER.NAME_H4 ||
      children[0]?.attribs?.style?.includes('18')
    ) {
      if (hasLineBreakChild(children)) {
        return (
          <>
            {toChildrenWithoutLineBreaks(children)?.map((child) => (
              <Heading
                key={uid(child)}
                as="h4"
                className="mb-2 mt-6"
                size="size5"
              >
                {child?.data?.trim() || child?.children[0]?.data?.trim()}
              </Heading>
            ))}
          </>
        );
      }

      return (
        <Heading as="h4" className="mb-2 mt-6" size="size5">
          {domToReact(children, pageDescriptionOptions)}
        </Heading>
      );
    }

    if (name === HTML_PARSER.NAME_PARAGRAPH) {
      const childrenWithoutLineBreaks = toChildrenWithoutLineBreaks(children);

      if (childrenWithoutLineBreaks.length === 0) {
        return <></>;
      }

      return (
        <Text>
          {domToReact(childrenWithoutLineBreaks, pageDescriptionOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_DIV && children.length > 0) {
      return <Text>{domToReact(children, pageDescriptionOptions)}</Text>;
    }

    if (
      name === HTML_PARSER.NAME_BR &&
      doesAnyDomNodeParentContainName(domNode?.parent, HTML_PARSER.NAME_DIV)
    ) {
      return <br />;
    }

    if (name === HTML_PARSER.NAME_HREF) {
      const { href } = attribs;
      const hrefTextContent = (
        domNode.lastChild?.data ||
        children[0]?.data ||
        children[0]?.children[0]?.data ||
        ''
      ).trim();

      if (!href || !hrefTextContent) {
        return null;
      }

      if (isUrlValidForInternalUsage(href)) {
        return (
          <NextLink
            href={toInternalLink(sanitize(href))}
            prefetch={false}
            passHref
          >
            <Anchor>{hrefTextContent}</Anchor>
          </NextLink>
        );
      }

      return (
        <Anchor
          href={forceHttps(href)}
          rel="noreferrer noopener"
          target="_blank"
        >
          {hrefTextContent}
        </Anchor>
      );
    }

    if ([HTML_PARSER.NAME_STRONG, HTML_PARSER.NAME_BOLD].includes(name)) {
      if (children.length === 0) {
        return <></>;
      }

      return <b>{domToReact(children, pageDescriptionOptions)}</b>;
    }

    if (name === HTML_PARSER.NAME_EM) {
      return (
        <Text as="em" italic>
          {domToReact(children, pageDescriptionOptions)}
        </Text>
      );
    }

    if (name === HTML_PARSER.NAME_LIST_ITEM) {
      return (
        <ListItem>{domToReact(children, pageDescriptionOptions)}</ListItem>
      );
    }

    if (
      [HTML_PARSER.NAME_UNORDERED_LIST, HTML_PARSER.NAME_ORDERED_LIST].includes(
        name
      )
    ) {
      const isOrderedList = children[0].name === HTML_PARSER.NAME_ORDERED_LIST;

      return (
        <ListGroup
          className="mb-1 space-y-2"
          type={isOrderedList ? 'ordered' : 'unordered'}
        >
          {domToReact(children, pageDescriptionOptions)}
        </ListGroup>
      );
    }

    if (name === HTML_PARSER.NAME_IMAGE) {
      const { src, alt = 'imagem genérica' } = attribs;

      return (
        <Image
          alt={toFallbackImageAltText(alt)}
          className="mx-auto my-4 h-auto w-full"
          height={192}
          src={src}
          width={343}
          aria-hidden
        />
      );
    }

    return <></>;
  },
};

const toPageDescription = (pageDescription) =>
  parse(pageDescription, pageDescriptionOptions);

export default toPageDescription;
